import find from 'lodash/find'
import map from 'lodash/map'
import React, { FC, memo, ReactNode, useMemo } from 'react'

import { RTEContent } from '@Components/RTEContent'
import { BaseAttribute, Product } from '@Generated/graphql'
import { useLocale } from '@Globals/Locale'

import { ProductDescriptionDrawer } from './Drawer'

interface ProductDescriptionProps {
  product?: Product
}

export const ProductDescription: FC<ProductDescriptionProps> = memo(({ product }) => {
  const locale = useLocale()

  const description = useMemo<ReactNode>(() => {
    if (!product) {
      return null
    }

    const descriptionGenerated = (
      (find(product?.masterVariant?.attributes, ['key', 'descriptionGenerated']) as BaseAttribute)?.value || undefined
    ) as Record<string, string> | undefined

    if (descriptionGenerated?.[locale]) {
      try {
        const parsed = JSON.parse(descriptionGenerated[locale])
        if (typeof parsed === 'object' && 'rte' in parsed && parsed.rte) {
          return (
            <RTEContent data={{ key: 'product.description', value: parsed.rte }} />
          )
        }
      } catch {
        // ignore errors
      }
    }

    if (!product?.description) {
      return null
    }

    return map(
      product.description.trim().split('\n'),
      (line, index) => {
        const trimmedData = line.trim()
        return trimmedData.length
          ? <p key={`${index}${line}`}>{trimmedData}</p>
          : null
      },
    )
  }, [locale, product])

  if (!product || !description) {
    return null
  }

  return <ProductDescriptionDrawer productID={product.id} productType={product.type}>{description}</ProductDescriptionDrawer>
})
